import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Pagination,
  Chip,
} from '@mui/material';
import { RateReview, Search, Sort } from '@mui/icons-material';
import { Review, ReviewsResponse } from '../types';
import { reviewsAPI } from '../services/api';
import ReviewCard from '../components/ReviewCard';

const ReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const [averageRating, setAverageRating] = useState(0);

  const itemsPerPage = 10;

  useEffect(() => {
    loadReviews();
  }, [page, sortBy]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(''); // Очищаем предыдущие ошибки
      const response: ReviewsResponse = await reviewsAPI.getAll(page, sortBy);
      setReviews(response.data);
      setTotalPages(Math.ceil(response.meta.total_reviews / itemsPerPage));
      setTotalReviews(response.meta.total_reviews);
      setAverageRating(response.meta.average_rating);
    } catch (err: any) {
      console.error('Error loading reviews:', err);
      // Проверяем, является ли это ошибкой "нет отзывов" или реальной ошибкой
      if (err.response?.status === 404 || err.response?.data?.detail === 'Not Found') {
        setReviews([]);
        setTotalPages(1);
        setTotalReviews(0);
        setAverageRating(0);
        setError(''); // Не показываем ошибку, если просто нет отзывов
      } else {
        setError('Ошибка загрузки отзывов');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const filteredReviews = reviews.filter(review =>
    review.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    review.comment.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (review.advantages && review.advantages.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (review.disadvantages && review.disadvantages.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Все отзывы
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Typography variant="body1" color="text.secondary">
            Просматривайте отзывы пользователей о различных продуктах
          </Typography>
          <Chip 
            icon={<RateReview />} 
            label={`${totalReviews} отзывов`} 
            color="primary" 
            variant="outlined" 
          />
          <Chip 
            label={`Средний рейтинг: ${averageRating.toFixed(1)}`} 
            color="secondary" 
            variant="outlined" 
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Фильтры и поиск */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Поиск отзывов"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <TextField
                select
                fullWidth
                label="Сортировка"
                value={sortBy}
                onChange={handleSortChange}
                InputProps={{
                  startAdornment: <Sort sx={{ mr: 1, color: 'action.active' }} />,
                }}
              >
                <MenuItem value="date_desc">Сначала новые</MenuItem>
                <MenuItem value="date_asc">Сначала старые</MenuItem>
                <MenuItem value="rating_desc">Сначала высокий рейтинг</MenuItem>
                <MenuItem value="rating_asc">Сначала низкий рейтинг</MenuItem>
              </TextField>
            </Box>
          </Box>
        </Box>

        {/* Список отзывов */}
        <Box sx={{ mb: 4 }}>
          {filteredReviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </Box>

        {/* Пагинация */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}

        {filteredReviews.length === 0 && !loading && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {searchTerm ? 'Отзывы не найдены' : 'Отзывов нет'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'Попробуйте изменить параметры поиска' : 'Пока никто не оставил отзывов'}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ReviewsPage;